import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';
import { ChangeEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { setIsError } from 'redux/ApiResponseStatusGlobalDisplay/slice';
import { getSelectedPermissionId } from 'redux/User/selectors';
import { updateInvoiceNotificationSetting } from 'redux/User/slice';
import { upsertInvoiceNotificationSetting } from 'services/api/invoiceNotification/client';
import {
  InvoiceNotificationSetting,
  SummarySchedule,
} from 'services/api/invoiceNotification/types';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';
import { logger } from 'services/logs/logger';

import {
  describeSchedule,
  formatNextRun,
  formatTimezone,
  getBrowserTimezone,
  LEGACY_SCHEDULE,
  LEGACY_TIMEZONE,
} from './SummaryScheduleDialog/summarySchedule';
import { SummaryScheduleDialog } from './SummaryScheduleDialog/SummaryScheduleDialog';
import {
  SummaryScheduleContainer,
  SummaryScheduleNextRun,
  SummaryScheduleText,
} from './UserCardPopover.style';

interface InvoiceNotificationToggleProps {
  invoiceNotificationSetting?: InvoiceNotificationSetting;
}

type SummaryScheduleState = {
  summarySchedule: SummarySchedule | null;
  timezone: string | null;
  nextRunAt: string | null;
};

export const InvoiceNotificationToggle = ({
  invoiceNotificationSetting,
}: InvoiceNotificationToggleProps): JSX.Element => {
  const dispatch = useDispatch();
  const { locale } = useIntl();
  const invoiceNotificationSettingIntl = useIntlByKey('header.notification');
  const scheduleIntl = useIntlByKey('header.notification.schedule');
  const permissionId = useSelector(getSelectedPermissionId);

  const [notificationSetting, setNotificationSetting] = useState({
    permissionId: invoiceNotificationSetting?.permissionId ?? permissionId,
    isEnabled: Boolean(invoiceNotificationSetting?.isEnabled),
    notificationType: Boolean(invoiceNotificationSetting?.notificationType),
  });
  const [summaryScheduleState, setSummaryScheduleState] = useState<SummaryScheduleState>({
    summarySchedule: invoiceNotificationSetting?.summarySchedule ?? null,
    timezone: invoiceNotificationSetting?.timezone ?? null,
    nextRunAt: invoiceNotificationSetting?.nextRunAt ?? null,
  });
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  // Settings saved before the schedule was configurable run with the legacy schedule
  const effectiveSchedule = summaryScheduleState.summarySchedule ?? LEGACY_SCHEDULE;
  const effectiveTimezone = summaryScheduleState.timezone ?? LEGACY_TIMEZONE;

  const saveSetting = async (
    setting: typeof notificationSetting,
    schedule?: { summarySchedule: SummarySchedule; timezone: string },
  ) => {
    const payload: InvoiceNotificationSetting = {
      permissionId: Number(setting.permissionId),
      isEnabled: Number(setting.isEnabled),
      notificationType: Number(setting.notificationType),
      ...schedule,
    };
    const response = await upsertInvoiceNotificationSetting(payload);
    const scheduleState = {
      summarySchedule: response?.summarySchedule ?? null,
      timezone: response?.timezone ?? null,
      nextRunAt: response?.nextRunAt ?? null,
    };
    setSummaryScheduleState(scheduleState);
    dispatch(updateInvoiceNotificationSetting({ ...payload, ...scheduleState }));
  };

  const handleInvoiceNotificationSettingChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const prevSetting = { ...notificationSetting };
    const setting = {
      ...notificationSetting,
      [event.target.name]: event.target.checked,
    };
    setNotificationSetting(setting);
    try {
      await saveSetting(setting);
      // First switch to summary: let the user pick when to receive it
      if (
        event.target.name === 'notificationType' &&
        setting.notificationType &&
        !summaryScheduleState.summarySchedule
      ) {
        setIsScheduleDialogOpen(true);
      }
    } catch (error: unknown) {
      logger.logError('Error upsertInvoiceNotificationSetting:', error);
      dispatch(setIsError(true));
      setNotificationSetting(prevSetting);
    }
  };

  const handleScheduleSave = async (summarySchedule: SummarySchedule, timezone: string) => {
    setIsSavingSchedule(true);
    try {
      await saveSetting(notificationSetting, { summarySchedule, timezone });
      setIsScheduleDialogOpen(false);
    } catch (error: unknown) {
      logger.logError('Error upsertInvoiceNotificationSetting schedule:', error);
      dispatch(setIsError(true));
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const isSummaryEnabled = notificationSetting.isEnabled && notificationSetting.notificationType;

  return (
    <div>
      <FormControlLabel
        name="isEnabled"
        control={
          <Switch
            color="primary"
            checked={notificationSetting.isEnabled}
            onChange={handleInvoiceNotificationSettingChange}
          />
        }
        label={invoiceNotificationSettingIntl('invoice-notification')}
        labelPlacement="start"
      />

      {notificationSetting.isEnabled && (
        <Grid component="label" container alignItems="center">
          <Grid item>{invoiceNotificationSettingIntl('invoice-individual')}</Grid>
          <Grid item>
            <Switch
              name="notificationType"
              color="default"
              checked={notificationSetting.notificationType}
              onChange={handleInvoiceNotificationSettingChange}
            />
          </Grid>
          <Grid item>{invoiceNotificationSettingIntl('invoice-summary')}</Grid>
        </Grid>
      )}

      {isSummaryEnabled && (
        <SummaryScheduleContainer>
          <SummaryScheduleText>
            {scheduleIntl('summary-line', {
              schedule: describeSchedule(effectiveSchedule, locale, scheduleIntl),
              timezone: formatTimezone(effectiveTimezone),
            })}{' '}
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={() => setIsScheduleDialogOpen(true)}
            >
              {scheduleIntl('edit')}
            </Link>
          </SummaryScheduleText>
          {summaryScheduleState.nextRunAt && (
            <SummaryScheduleNextRun>
              {scheduleIntl('next-run', {
                date: formatNextRun(summaryScheduleState.nextRunAt, effectiveTimezone, locale),
              })}
            </SummaryScheduleNextRun>
          )}
        </SummaryScheduleContainer>
      )}

      <SummaryScheduleDialog
        isOpen={isScheduleDialogOpen}
        schedule={effectiveSchedule}
        timezone={summaryScheduleState.timezone ?? getBrowserTimezone()}
        isSaving={isSavingSchedule}
        onClose={() => setIsScheduleDialogOpen(false)}
        onSave={handleScheduleSave}
      />
    </div>
  );
};
